import { Platform } from "quasar";
import { useCommonStore } from "src/stores/common-store";
import { getAccountID, goToHomeScreen } from "src/utils/commonFunction";
import { inIframe } from "src/utils";

// Set once we have sent the browser to a store listing, so returning from the
// Play Store / App Store cannot bounce the user straight back out again.
const STORE_REDIRECT_KEY = "lns-deeplink-store-redirect";

// Where a deep link was heading when the auth guard bounced it to login.
// localStorage, not pinia: OIDC login leaves and re-enters the app, which drops
// in-memory state. Stale entries are dropped rather than fired at the user
// after an unrelated login days later.
const PENDING_KEY = "lns-deeplink-pending";
const PENDING_TTL_MS = 30 * 60 * 1000;

// The URL the browser was opened with. Captured at module load so that a later
// in-app navigation to the same path is not mistaken for a deep-link arrival.
const entryPath =
  typeof window !== "undefined" && window.location
    ? window.location.pathname
    : "";

/**
 * The paths a deep link is allowed to target. Kept here so the cordova
 * handlers (boot/filters.js, boot/universal-links.js) and the web fallback all
 * agree on what counts as a deep link.
 */
export const isDeepLinkPath = (path) => {
  if (!path) return false;

  const parts = path.split("/").filter(Boolean);

  if (parts[0] === "store-detail" && parts[1] && parts[2] === "my-deal")
    return true;

  if (path === "/my-deals") return true;

  return path.includes("store-detail") && path.includes("deal-detail/store");
};

// /store-detail/{storeId}/online-discount/{discountId}
const ONLINE_DISCOUNT_PATH =
  /^\/store-detail\/[^/]+\/online-discount\/[^/]+\/?$/;

/**
 * Paths a universal link (https) may open: every deep-link path plus the
 * online discount detail page. The custom URL scheme handler keeps using
 * isDeepLinkPath, so online discounts are reachable by universal link only.
 */
export const isUniversalLinkPath = (path) =>
  isDeepLinkPath(path) || ONLINE_DISCOUNT_PATH.test(path || "");

/**
 * Public links that carry their own token and need no login, currently the
 * phone verification link.
 *
 * Deliberately NOT part of isDeepLinkPath: that one also drives the app-store
 * fallback and the login resume. A verification link must render where it was
 * opened - bouncing the user to the store, or through login first, abandons
 * the verification they were asked to complete.
 */
export const isPublicLinkPath = (path) => {
  if (!path) return false;

  // White-label only. ACCOUNT_ID is baked into white-label native builds, so
  // this holds on a cold start before the store configuration has loaded; the
  // marketplace has neither and keeps its previous handling of the link.
  if (!getAccountID() && !process.env.ACCOUNT_ID) return false;

  const parts = path.split("/").filter(Boolean);

  return parts[0] === "verify-phone" && !!parts[1];
};

/**
 * Rewrites a matched deep-link path for the build it landed in.
 *
 * /my-deals lists deals across every store, which only makes sense on the
 * marketplace. A white-label build is a single store, so that page does not
 * exist there - send it to the store's own deals page instead.
 *
 * Native only. On web /my-deals resolves correctly on its own and is left
 * alone.
 */
export const resolveDeepLinkPath = (path) => {
  if (!path) return path;

  if (path === "/my-deals" && Platform.is.cordova) {
    const accountID = getAccountID();
    if (accountID) return `/store-detail/${accountID}/my-deal`;
  }

  return path;
};

/** Store listing for the platform we are actually running on, if configured. */
export const getNativeAppLink = () => {
  const commonStore = useCommonStore();

  if (Platform.is.android) return commonStore.appAndroidLink || null;
  if (Platform.is.ios) return commonStore.appIosLink || null;

  return null;
};

/**
 * A deep link opened without the app installed falls back to the browser. When
 * that happens on a phone and the store is set up for a native app, send the
 * user to the store listing instead of rendering the page.
 *
 * Must run after getStoreConfiguration() has resolved, otherwise storeDetails,
 * the account id and the store links are all still empty.
 *
 * @returns {boolean} true when a redirect was started.
 */
export const redirectDeepLinkToAppStore = () => {
  try {
    // Inside the app already, or nothing to install.
    if (Platform.is.cordova || !Platform.is.mobile) return false;

    if (inIframe()) return false;

    // Installed as a PWA - treat that as having the app.
    if (window.matchMedia("(display-mode: standalone)").matches) return false;

    if (!isDeepLinkPath(entryPath)) return false;

    if (sessionStorage.getItem(STORE_REDIRECT_KEY)) return false;

    const commonStore = useCommonStore();

    if (!Object.keys(commonStore.storeDetails || {}).length) return false;
    if (!getAccountID()) return false;

    const appLink = getNativeAppLink();
    if (!appLink) return false;

    sessionStorage.setItem(STORE_REDIRECT_KEY, "1");
    window.location.href = appLink;

    return true;
  } catch (error) {
    console.log("DeepLink Store Redirect Error", error);
    return false;
  }
};

/**
 * Remember where a deep link was going, so login can resume it.
 * Call with the full path (query included) before redirecting to login.
 */
export const setPendingDeepLink = (fullPath) => {
  try {
    if (!fullPath) return;
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ path: fullPath, ts: Date.now() })
    );
  } catch (error) {
    console.log("DeepLink Pending Write Error", error);
  }
};

/** Read the pending deep link and clear it. Returns null when there is none. */
export const takePendingDeepLink = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;

    localStorage.removeItem(PENDING_KEY);

    const pending = JSON.parse(raw);
    if (!pending || !pending.path) return null;
    if (Date.now() - pending.ts > PENDING_TTL_MS) return null;

    return pending.path;
  } catch (error) {
    console.log("DeepLink Pending Read Error", error);
    return null;
  }
};

/**
 * Landing screen after a successful login: the deep link the user originally
 * followed, or Home when there was not one.
 */
export const goToPendingDeepLinkOrHome = (router) => {
  const pendingPath = takePendingDeepLink();

  if (pendingPath) {
    try {
      const navigation = router.push({ path: pendingPath });

      // router.push settles asynchronously, so the catch below cannot see a
      // refused navigation - fall back to Home when the push is rejected.
      if (navigation && typeof navigation.catch === "function") {
        navigation.catch((error) => {
          console.log("DeepLink Resume Error", error);
          goToHomeScreen(router);
        });
      }

      return;
    } catch (error) {
      console.log("DeepLink Resume Error", error);
    }
  }

  goToHomeScreen(router);
};
